import React from "react";
import { useAppSelector } from "@/hooks/useAppSelector";

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  requiredRole: "localAdmin" | "superAdmin";
  fallbackMessage?: string;
}

/**
 * Component to protect routes based on user roles
 * - localAdmin: Organization-level administrator
 * - superAdmin: Global administrator across all organizations
 */
const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({
  children,
  requiredRole,
  fallbackMessage,
}) => {
  const { user } = useAppSelector((state) => state.system);

  const hasAccess = () => {
    switch (requiredRole) {
      case "localAdmin":
        return user?.isLocalAdmin;
      case "superAdmin":
        return user?.isGlobalAdmin;
      default:
        return false;
    }
  };

  if (!hasAccess()) {
    const defaultMessage =
      requiredRole === "superAdmin"
        ? "Access denied. Global Administrator privileges required."
        : "Access denied. Organization Administrator privileges required.";

    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center rounded p-8 shadow-sm">
        <div className="max-w-md text-center">
          <div className="mb-4 text-6xl">🔒</div>
          <h2 className="mb-2 text-xl font-semibold text-neutral-100">Access Restricted</h2>
          <p className="mb-4 text-neutral-200">{fallbackMessage || defaultMessage}</p>
          <div className="text-sm text-neutral-200">
            Please contact your system administrator if you believe you should have access to this
            area.
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default RoleProtectedRoute;
